import { LitElement, html } from './-global.min.js'

// Shared cache across all instances — keyed by URL, value is parsed DOM Document
const filterCache = new Map()

class HHBlogFilters extends LitElement {
  static properties = {}

  constructor() {
    super()

    this.section = this.dataset.section
    this._handleFilterClick = this._handleFilterClick.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    setTimeout(() => {
      this.handleInitialScroll()
    }, 10)

    const sectionId = document.querySelector('main')?.querySelector('section')?.getAttribute('id') ?? ''
    if (this.section && sectionId.includes(this.section)) {
      document.querySelector('.js-header-bg')?.classList.add('tbbt')
      this.closest('.main-blog')?.classList.add('main-blog__padding')
    }

    this.addEventListener('click', this._handleFilterClick)
    this._prefetchFilters()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this._handleFilterClick)
  }

  handleInitialScroll() {
    const isScrollable = this.scrollWidth > this.clientWidth
    if (!isScrollable) return

    const activeTFilter = this.querySelector('.active-filter')
    this.scrollLeft = activeTFilter?.offsetLeft - 20
  }

  // Prefetch all filter URLs in the background without blocking the page
  _prefetchFilters() {
    const urls = [...new Set(
      [...this.querySelectorAll('[data-filter-url]')]
        .map((el) => el.dataset.filterUrl)
        .filter(Boolean)
    )]

    const prefetchOne = async (url) => {
      if (filterCache.has(url)) return
      try {
        const res = await fetch(url)
        const text = await res.text()
        filterCache.set(url, new DOMParser().parseFromString(text, 'text/html'))
      } catch {
        // Silently ignore — will fetch on demand when clicked
      }
    }

    const run = () => urls.forEach(prefetchOne)

    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 4000 })
    } else {
      setTimeout(run, 2000)
    }
  }

  // Fetch a filter URL, using the cache when available
  async _fetchFilter(url) {
    if (filterCache.has(url)) return filterCache.get(url)

    const res = await fetch(url)
    const text = await res.text()
    const dom = new DOMParser().parseFromString(text, 'text/html')
    filterCache.set(url, dom)
    return dom
  }

  // Resolves when the element's CSS transition ends (or after a safety timeout)
  _waitForTransition(el) {
    return Promise.race([
      new Promise((resolve) => el.addEventListener('transitionend', resolve, { once: true })),
      new Promise((resolve) => setTimeout(resolve, 350)),
    ])
  }

  async _handleFilterClick(e) {
    const filterEl = e.composedPath().find(
      (el) => el instanceof Element && el.dataset?.filterUrl
    )

    if (!filterEl) return

    const url = filterEl.dataset.filterUrl
    if (!url) return

    e.preventDefault()

    const mainBlog = this.closest('.main-blog')
    const articlesGrid = mainBlog.querySelector('.blog-articles.js-product-grid-list')

    try {
      // Phase 1: dim grid while fetching (loading indicator)
      articlesGrid?.classList.add('is-loading')

      const dom = await this._fetchFilter(url)

      // Phase 2: fade to invisible before swapping so the content change isn't jarring
      articlesGrid?.classList.add('is-swapping')
      if (articlesGrid) await this._waitForTransition(articlesGrid)

      // Phase 3: swap content while grid is invisible
      const newArticles = dom.querySelector('.blog-articles.js-product-grid-list')
      if (newArticles && articlesGrid) {
        articlesGrid.innerHTML = newArticles.innerHTML
      }

      const newLoadMoreContainer = dom.querySelector('.js-load-more-container')
      const currentLoadMoreContainer = mainBlog.querySelector('.js-load-more-container')
      if (newLoadMoreContainer) {
        if (currentLoadMoreContainer) {
          currentLoadMoreContainer.outerHTML = newLoadMoreContainer.outerHTML
        } else {
          articlesGrid?.insertAdjacentHTML('afterend', newLoadMoreContainer.outerHTML)
        }
      } else if (currentLoadMoreContainer) {
        currentLoadMoreContainer.remove()
      }

      // Update active state — toggle CSS class on the wrapper and show-underline
      // attribute on the hh-button so its shadow ::after underline follows the selection
      this.querySelectorAll('[data-filter-url]').forEach((el) => {
        const isActive = el.dataset.filterUrl === url
        el.classList.toggle('active-filter', isActive)
        el.querySelector('hh-button')?.toggleAttribute('show-underline', isActive)
      })

      window.history.pushState({}, '', url)
      this.handleInitialScroll()
    } catch (err) {
      console.error('Blog filter error:', err)
      window.location.href = url
    } finally {
      // Phase 4: fade back in
      const grid = mainBlog.querySelector('.blog-articles.js-product-grid-list')
      grid?.classList.remove('is-loading', 'is-swapping')
    }
  }

  render() {
    return html`<slot></slot> `
  }
}

customElements.define('hh-blog-filters', HHBlogFilters)
